import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isAppError } from '@/lib/errors';
import { authService } from '@/services/auth';
import { useToast } from '@/hooks/use-toast';
import { isInvitationAlreadyUsed, isInvitationExpiredPending } from '@/utils/invitationToken';

type InvitationTokenStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'unavailable';

export function useSignupInvitationToken() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [status, setStatus] = useState<InvitationTokenStatus>(() =>
    token ? 'checking' : 'valid'
  );
  const [isResending, setIsResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('valid');
      return;
    }

    let cancelled = false;

    const verify = async () => {
      setStatus('checking');
      setResendSent(false);

      try {
        const response = await authService.verifyToken({ token });
        if (!cancelled) {
          if (response.valid) {
            setStatus('valid');
            return;
          }
          if (isInvitationExpiredPending(response)) {
            setStatus('invalid');
            return;
          }
          setStatus('unavailable');
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        if (isInvitationAlreadyUsed(undefined, error)) {
          setStatus('unavailable');
          return;
        }

        if (isAppError(error) && (error.code === 'AUTH_TOKEN_INVALID' || error.code === 'VALIDATION_ERROR')) {
          setStatus('invalid');
          return;
        }

        setStatus('invalid');
        toast({
          variant: 'error',
          title: 'Erro ao validar link',
          description: 'Não foi possível validar o link de cadastro. Tente novamente.',
        });
      }
    };

    void verify();

    return () => {
      cancelled = true;
    };
  }, [token, toast]);

  const resendInvitation = useCallback(
    async (email: string) => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        toast({
          variant: 'error',
          title: 'E-mail obrigatório',
          description: 'Informe o e-mail para reenviar o link de cadastro.',
        });
        return;
      }

      setIsResending(true);
      try {
        await authService.resendInvitation({ email: trimmedEmail });
        setResendSent(true);
        toast({
          variant: 'success',
          title: 'Link reenviado',
          description: 'Se o e-mail existir, enviamos um novo link.',
        });
      } catch {
        toast({
          variant: 'error',
          title: 'Erro ao reenviar link',
          description: 'Tente novamente mais tarde.',
        });
      } finally {
        setIsResending(false);
      }
    },
    [toast]
  );

  return {
    token,
    emailFromUrl,
    status,
    isChecking: status === 'checking',
    isInvalid: status === 'invalid',
    isUnavailable: status === 'unavailable',
    isValid: status === 'valid',
    isResending,
    resendSent,
    resendInvitation,
  };
}
